/** @jsxImportSource solid-js */

import { customElement, noShadowDOM } from "solid-element";
import { createSignal, Match, Switch } from "solid-js";
import { Step_0_NotListed } from "./0-funnel.solid";
import { Step_1_Institution } from "./1-affiliation.solid";
import { Step_2_Email } from "./2-verification.solid";
import { Step_3_Account } from "./3-credentials.solid";
import { Step_4_Recovery } from "./4-recovery.solid";
import { Step_5_Demographics } from "./5-demographics.solid";
import { Sidebar } from "./sidebar.solid";
import { Progressbar } from "#root/lib/palette/components/progress.solid.js";
import callistoLogo from "../../assets/Callisto_logo.png";
import { ErrorCallout } from "#root/lib/palette/components/callout.solid.js";

export function SignupNewExperience(props: { onFinish: () => void }) {
  const [currentProgress, setCurrentProgress] = createSignal(2);
  const [verificationToken, setVerificationToken] = createSignal("");
  const [phoneNumber, setPhoneNumber] = createSignal("");
  const [email, setEmail] = createSignal("");
  const [username, setUsername] = createSignal("");
  const [password, setPassword] = createSignal("");
  const [errorState, setErrorState] = createSignal(false);
  const [errorMessage, setErrorMessage] = createSignal("");

  function reset() {
    setCurrentProgress(2);
  }

  function goUnknown() {
    setCurrentProgress(0);
  }

  function goNext() {
    setCurrentProgress(currentProgress() + 1);
  }

  // Error-handling ////////////////////////////////////////////////////////////
  function setError(message: string) {
    setErrorState(true);
    setErrorMessage(message);
  }

  function resetError() {
    setErrorState(false);
    setErrorMessage("");
  }

  return (
    <div class="flex">
      <Sidebar currentProgress={currentProgress} />
      <main class="w-full mx-auto grid grid-cols-1 items-center h-screen mx-2 bg-brand-100 gap-5 overflow-y-scroll">
        <div class="xl:w-xl mx-auto w-full grow px-5 my-5 pb-16 xl:pb-5">
          <a
            href="/"
            class="flex xl:hidden text-center w-full justify-center my-10 h-40"
          >
            <img src={callistoLogo} alt="" />
          </a>
          <div class="xl:hidden">
            <Progressbar
              segments={4}
              complete={currentProgress() - 1}
              upcomingColor="bg-brand-300"
            />
          </div>

          {errorState() && (
            <ErrorCallout title="We couldn't create your account">
              {errorMessage()}
            </ErrorCallout>
          )}

          <Switch>
            <Match when={currentProgress() === 0}>
              <Step_0_NotListed reset={reset} />
            </Match>
            <Match when={currentProgress() === 1}>
              <Step_1_Institution goNext={goNext} goUnknown={goUnknown} />
            </Match>
            <Match when={currentProgress() === 2}>
              <Step_2_Email
                goNext={goNext}
                goUnknown={goUnknown}
                setEmail={setEmail}
                setVerificationToken={setVerificationToken}
                setError={setError}
                resetError={resetError}
              />
            </Match>
            <Match when={currentProgress() === 3}>
              <Step_3_Account
                goNext={goNext}
                setPhone={setPhoneNumber}
                setPassword={setPassword}
                setUsername={setUsername}
                setError={setError}
                resetError={resetError}
              />
            </Match>
            <Match when={currentProgress() === 4}>
              <Step_4_Recovery
                goNext={goNext}
                currentPhoneNumber={phoneNumber}
                currentEmail={email}
                verificationToken={verificationToken}
                currentUsername={username}
                currentPassword={password}
                setError={setError}
                resetError={resetError}
              />
            </Match>
            <Match when={currentProgress() === 5}>
              <Step_5_Demographics
                goNext={props.onFinish}
                setError={setError}
                resetError={resetError}
              />
            </Match>
          </Switch>
        </div>
      </main>
    </div>
  );
}

// This shim exists for Solid-in-React support as I port things.
// TODO: remove this.
customElement("signup-new-experience", { finish: () => {} }, (props) => {
  noShadowDOM();

  return <SignupNewExperience onFinish={props.finish} />;
});
